import { motion } from 'motion/react';
import { ArrowLeft, Search } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { useState, useEffect } from 'react';
import { FlagImage, countryToFlagCode } from './utils/countryFlags';
import { CountryName } from './ui/CountryName';
import { ParallaxBackground } from './utils/parallax';
import { db, auth } from '../services/firebaseConfig';
import { getDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useScreenReady } from '../contexts/ScreenTransitionContext';

interface AllCountriesScreenProps {
  onBack: () => void;
}

const allCountries = [
  // Top African Countries
  { name: 'Nigeria', flag: '🇳🇬', region: 'West Africa' },
  { name: 'Ghana', flag: '🇬🇭', region: 'West Africa' },
  { name: 'South Africa', flag: '🇿🇦', region: 'Southern Africa' },
  { name: 'Kenya', flag: '🇰🇪', region: 'East Africa' },
  { name: 'Ethiopia', flag: '🇪🇹', region: 'East Africa' },
  { name: 'Tanzania', flag: '🇹🇿', region: 'East Africa' },

  // More African Countries
  { name: 'Egypt', flag: '🇪🇬', region: 'North Africa' },
  { name: 'Morocco', flag: '🇲🇦', region: 'North Africa' },
  { name: 'Algeria', flag: '🇩🇿', region: 'North Africa' },
  { name: 'Uganda', flag: '🇺🇬', region: 'East Africa' },
  { name: 'Cameroon', flag: '🇨🇲', region: 'Central Africa' },
  { name: 'Senegal', flag: '🇸🇳', region: 'West Africa' },
  { name: 'Rwanda', flag: '🇷🇼', region: 'East Africa' },
  { name: 'Tunisia', flag: '🇹🇳', region: 'North Africa' },
  { name: 'Zimbabwe', flag: '🇿🇼', region: 'Southern Africa' },
  { name: 'Botswana', flag: '🇧🇼', region: 'Southern Africa' },
  { name: 'Ivory Coast', flag: '🇨🇮', region: 'West Africa' },
  { name: 'Zambia', flag: '🇿🇲', region: 'Southern Africa' },
  { name: 'Mozambique', flag: '🇲🇿', region: 'Southern Africa' },
  { name: 'Mali', flag: '🇲🇱', region: 'West Africa' },
  { name: 'Burkina Faso', flag: '🇧🇫', region: 'West Africa' },
  { name: 'Niger', flag: '🇳🇪', region: 'West Africa' },
  { name: 'Angola', flag: '🇦🇴', region: 'Central Africa' },
  { name: 'Somalia', flag: '🇸🇴', region: 'East Africa' },
  { name: 'Chad', flag: '🇹🇩', region: 'Central Africa' },
  { name: 'Sudan', flag: '🇸🇩', region: 'North Africa' },
  { name: 'Benin', flag: '🇧🇯', region: 'West Africa' },
  { name: 'Togo', flag: '🇹🇬', region: 'West Africa' },
  { name: 'Sierra Leone', flag: '🇸🇱', region: 'West Africa' },
  { name: 'Liberia', flag: '🇱🇷', region: 'West Africa' },
  { name: 'Namibia', flag: '🇳🇦', region: 'Southern Africa' },
  { name: 'Mauritius', flag: '🇲🇺', region: 'East Africa' },
  { name: 'Madagascar', flag: '🇲🇬', region: 'East Africa' },
  { name: 'Malawi', flag: '🇲🇼', region: 'Southern Africa' },
  { name: 'Gabon', flag: '🇬🇦', region: 'Central Africa' },
  { name: 'Mauritania', flag: '🇲🇷', region: 'West Africa' },
  { name: 'Guinea', flag: '🇬🇳', region: 'West Africa' },
  { name: 'Burundi', flag: '🇧🇮', region: 'East Africa' },
  { name: 'Eritrea', flag: '🇪🇷', region: 'East Africa' },
  { name: 'Gambia', flag: '🇬🇲', region: 'West Africa' },
  { name: 'Lesotho', flag: '🇱🇸', region: 'Southern Africa' },
  { name: 'eSwatini', flag: '🇸🇿', region: 'Southern Africa' },
  { name: 'Djibouti', flag: '🇩🇯', region: 'East Africa' },
  { name: 'Congo', flag: '🇨🇬', region: 'Central Africa' },
  { name: 'DR Congo', flag: '🇨🇩', region: 'Central Africa' },
  { name: 'Libya', flag: '🇱🇾', region: 'North Africa' },
  { name: 'Seychelles', flag: '🇸🇨', region: 'East Africa' },
  { name: 'Cape Verde', flag: '🇨🇻', region: 'West Africa' },
  { name: 'Comoros', flag: '🇰🇲', region: 'East Africa' },
  { name: 'Guinea-Bissau', flag: '🇬🇼', region: 'West Africa' },
  { name: 'Equatorial Guinea', flag: '🇬🇶', region: 'Central Africa' },
  { name: 'São Tomé', flag: '🇸🇹', region: 'Central Africa' },
  { name: 'South Sudan', flag: '🇸🇸', region: 'East Africa' },
  { name: 'CAR', flag: '🇨🇫', region: 'Central Africa' },

  // Caribbean Nations
  { name: 'Jamaica', flag: '🇯🇲', region: 'Caribbean' },
  { name: 'Belize', flag: '🇧🇿', region: 'Caribbean' },
  { name: 'Cuba', flag: '🇨🇺', region: 'Caribbean' },
  { name: 'Dominican Republic', flag: '🇩🇴', region: 'Caribbean' },
  { name: 'Guyana', flag: '🇬🇾', region: 'Caribbean' },
  { name: 'Saint Kitts and Nevis', flag: '🇰🇳', region: 'Caribbean' },
  { name: 'Guadeloupe', flag: '🇬🇵', region: 'Caribbean' },
  { name: 'Martinique', flag: '🇲🇶', region: 'Caribbean' },
  { name: 'French Guiana', flag: '🇬🇫', region: 'Caribbean' },
  { name: 'Puerto Rico', flag: '🇵🇷', region: 'Caribbean' },
  { name: 'Curacao', flag: '🇨🇼', region: 'Caribbean' },
  { name: 'Aruba', flag: '🇦🇼', region: 'Caribbean' },
  { name: 'Bonaire', flag: '🇧🇶', region: 'Caribbean' },
  { name: 'Bermuda', flag: '🇧🇲', region: 'Caribbean' },
  { name: 'Cayman Islands', flag: '🇰🇾', region: 'Caribbean' },
  { name: 'Turks and Caicos Islands', flag: '🇹🇨', region: 'Caribbean' },
  { name: 'US Virgin Islands', flag: '🇻🇮', region: 'Caribbean' },
  { name: 'Anguilla', flag: '🇦🇮', region: 'Caribbean' },
  { name: 'Montserrat', flag: '🇲🇸', region: 'Caribbean' },
  { name: 'Mayotte', flag: '🇾🇹', region: 'Africa' },
  { name: 'Saint Helena', flag: '🇸🇭', region: 'Africa' },
  { name: 'Suriname', flag: '🇸🇷', region: 'Caribbean' },
  { name: 'Trinidad & Tobago', flag: '🇹🇹', region: 'Caribbean' },
  { name: 'Haiti', flag: '🇭🇹', region: 'Caribbean' },
  { name: 'Barbados', flag: '🇧🇧', region: 'Caribbean' },
  { name: 'Bahamas', flag: '🇧🇸', region: 'Caribbean' },
  { name: 'Grenada', flag: '🇬🇩', region: 'Caribbean' },
  { name: 'Saint Lucia', flag: '🇱🇨', region: 'Caribbean' },
  { name: 'Dominica', flag: '🇩🇲', region: 'Caribbean' },
  { name: 'Saint Vincent', flag: '🇻🇨', region: 'Caribbean' },
  { name: 'Antigua', flag: '🇦🇬', region: 'Caribbean' },

  // African Diaspora / Regional
  { name: 'Black American/Soulaan', flag: 'soulaan', region: 'North American' },
  { name: 'America', flag: '🇺🇸', region: 'North American' },
  { name: 'European Africans', flag: '🇪🇺', region: 'European' },
  { name: 'UK Africans', flag: '🇬🇧', region: 'European' },
  { name: 'Republic of New Afrika', flag: 'rna', region: 'North American' },
  { name: 'Gullah Geechie', flag: 'gg', region: 'North American' },
  { name: 'Canada', flag: '🇨🇦', region: 'North American' },
  { name: 'France Africans', flag: '🇫🇷', region: 'European' },
  { name: 'Brazil', flag: '🇧🇷', region: 'South American' },
];

export function AllCountriesScreen({ onBack }: AllCountriesScreenProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [countryCounts, setCountryCounts] = useState<Record<string, string>>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(true);
  useScreenReady(!isLoadingCounts);

  // Country counts are pre-computed by the nightly rollup CF into platform_stats/global.
  // A single doc read replaces per-country queries (which are bracket-gated by the
  // Phase 2d security rule and cannot produce total cross-bracket counts).
  // We wait for a confirmed signed-in user before fetching — on iOS, Firebase auth
  // restoration from persistence can lag behind component mount, causing the read to
  // fail the isSignedIn() rule and return silently-empty results.
  useEffect(() => {
    let isActive = true;

    const fetchCounts = async () => {
      try {
        // Self-heal: trigger a server-side refresh first (10-min cooldown).
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const { getApp } = await import('firebase/app');
          const fn = httpsCallable(getFunctions(getApp()), 'refreshCountryCounts');
          const result = await fn();
          console.log('[AllCountries] refreshCountryCounts result:', result.data);
        } catch (e) {
          console.warn('[AllCountries] refreshCountryCounts FAILED:', (e as any)?.message, e);
        }
        const globalSnap = await getDoc(doc(db, 'platform_stats', 'global'));
        const raw: Record<string, number> = globalSnap.data()?.countryCounts ?? {};
        console.log('[AllCountries] raw countryCounts from doc:', raw);

        if (isActive) {
          const counts: Record<string, string> = {};
          allCountries.forEach(country => {
            const cCode = countryToFlagCode[country.name] || country.name.toLowerCase();
            // Try ISO code first (new users), then full country name (older users)
            const n = raw[cCode] ?? raw[country.name] ?? 0;
            counts[country.name] = n >= 1000 ? (n / 1000).toFixed(1) + 'K' : n.toString();
            (counts as any)[country.name + '_raw'] = n;
          });
          setCountryCounts(counts);
        }
      } catch (error) {
        console.error("Error fetching country counts:", error);
      } finally {
        if (isActive) setIsLoadingCounts(false);
      }
    };

    // Wait for auth to resolve before fetching — guards against the iOS race
    // where auth restoration completes after this component mounts.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && isActive) fetchCounts();
      else if (!user && isActive) setIsLoadingCounts(false);
    });

    return () => { isActive = false; unsubscribe(); };
  }, []);

  // Flat list of heritages: search-filtered, then drop any with 0 users,
  // then sort by yapper count descending.
  const sortedCountries = allCountries
    .filter((country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.region.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .map((c) => ({
      ...c,
      _count: parseInt((countryCounts as any)[c.name + '_raw'] || '0', 10),
    }))
    .filter((c) => c._count > 0)
    .sort((a, b) => b._count - a._count);

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] pb-20 relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-black/30 backdrop-blur-lg"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
          <div className="flex items-center gap-4 mb-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-[#2C3E50] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div>
              <h1 className="text-2xl">
                All Heritages
              </h1>
              <p className="text-sm text-[#373737] dark:text-[#E5E5E5]">
                {allCountries.length} heritages & regions
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
            <Input
              placeholder="Search countries or regions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-[#F5F5F5] dark:bg-[#1a1a1a] border-none text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] rounded-xl shadow-[2px_2px_0px_0px_rgba(0,0,0,0.25)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.1)]"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 6.5rem)' }}>
            {!isLoadingCounts && sortedCountries.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#666666] dark:text-[#999999]">
                No heritages with yappers yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {sortedCountries.map((country, index) => (
                  <motion.div
                    key={country.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(index * 0.02, 0.5) }}
                    className="p-4 rounded-xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10 hover:border-[#F39C12] transition-colors cursor-pointer card-shadow"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-6 rounded overflow-hidden shrink-0">
                          <FlagImage
                            countryName={country.name}
                            size="w80"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Display: drop the " Africans" suffix so diaspora entries
                            ("France Africans", "UK Africans" …) read as just the
                            country. The stored name still drives count lookups. */}
                        <CountryName
                          as="h3"
                          name={country.name.replace(/ Africans$/, '')}
                          className="text-black dark:text-[#E5E5E5] font-bold min-w-0"
                        />
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[#F39C12] dark:text-[#4478ea] font-medium">
                          {isLoadingCounts ? '...' : (countryCounts[country.name] || '0')}
                        </p>
                        <p className="text-xs text-[#666666] dark:text-[#E5E5E5]">{country._count === 1 ? 'yapper' : 'yappers'}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
